import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TabHeader from "../TabHeader";
import Details from "../../Tab/Details/Details";
import RelatedVtis from "../../Tab/RelatedVTIS/RelatedVTIS";
import ClamCloudInfo from "../../../../PagesComponents/AnalysisReports/ClamCloudMlResults/ClamCloudMlInfo";
import FileIcon from "../../../../Icon-SVG/FileIcon";
import SandboxIcon from "../../../../Icon-SVG/SandboxIcon";
import ActionButton from "../../../ActionButton/ActionButton";
import StaticInfoCardItem from "../../../../../Pages/AnalysisReports/Content/Tabs/StaticInfo/components/StaticInfoCardItem";
import InfoTable, {
  InfoRow,
} from "../../../../PagesComponents/AnalysisReports/InfoTable/InfoTable";
import Modal from "../../../../UI/Modal/Modal";
import Backdrop from "../../../../UI/Backdrop/Backdrop";
import SubmitPopup from "../../../SubmitPopup/SubmitPopup";
import Content from "../../../SubmitPopup/Content/Content";

import { prepareStaticInfoData } from "../../../../../Pages/AnalysisReports/Content/Tabs/StaticInfo/utils/staticInfoUtil";
import { desktopVmOptions } from "../../../SubmitPopup/constants";
import { prepareSubmitData } from "../../../SubmitPopup/utils";
import useSandboxSubmitGuard from "../../../SubmitPopup/useSandboxSubmitGuard";
import { RequestArtifactSubmission } from "../../../../../store/actions/analysisReportActions";

const checkShowSubmitButton = (type) => type === "Extracted Payload" || type === "Dropped File";

const getSource = (type) => {
  if (type === "Extracted Payload") return "extractedPayloads";
  else if (type === "Dropped File") return "droppedFiles";
  else return null;
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const DroppedFileListContent = ({ selectedFile, loading, error }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(true);
  const [staticInfoDataList, setStaticInfoDataList] = useState([]);
  const [submitPopupActive, setSubmitPopupActive] = useState(false);
  // Even though it is just one file, we have to declare and use it as an array bc SubmitPopup works with arrays.
  const [submitFile, setSubmitFile] = useState([]);
  const { hasLicence, availableVmOptions, canOpenSubmitPopup } = useSandboxSubmitGuard();
  const artifactSubmissionLoading = useSelector(
    (state) => state.analysisReports.artifactSubmissionLoading,
  );

  let query = useQuery();

  const collapseHandler = (title, subTitle) => {
    setStaticInfoDataList((prevState) =>
      prevState.map((cardData) => {
        if (cardData["title"] === title) {
          return {
            ...cardData,
            items: cardData["items"].map((cardDataItem) => {
              if (cardDataItem["subTitle"] === subTitle) {
                return {
                  ...cardDataItem,
                  collapsed: !cardDataItem["collapsed"],
                };
              }
              return cardDataItem;
            }),
          };
        }
        return cardData;
      }),
    );
  };

  useEffect(() => {
    setStaticInfoDataList([]);
    if (selectedFile) {
      const { safebrowsing, virustotal, yara, metadefender, clamav } = selectedFile;
      const subCategories = {
        safebrowsing,
        virustotal,
        yara,
        metadefender,
        clamav,
      };
      const subCategoryFields = Object.keys(subCategories);

      const staticInfoData = subCategoryFields.map((subCatField) =>
        prepareStaticInfoData(subCatField, subCategories),
      );
      setStaticInfoDataList(staticInfoData.filter((data) => data?.items?.length));
    }
  }, [selectedFile, prepareStaticInfoData]);

  const submitToSandboxHandler = () => {
    if (!canOpenSubmitPopup()) return;
    setSubmitPopupActive(true);
    setSubmitFile([
      {
        fileName: selectedFile?.value,
        fileHash: selectedFile?.sha256,
        source: getSource(selectedFile?.type),
        fileIDfull: query.get("data"),
      },
    ]);
  };

  const closePopupHandler = () => {
    setSubmitPopupActive(false);
  };

  const submitHandler = (selectedFiles) => {
    const preparedSubmitData = prepareSubmitData(selectedFiles, desktopVmOptions);
    dispatch(RequestArtifactSubmission(preparedSubmitData));
  };

  const renderContent = (
    currentFileIndex,
    activeOs,
    selectedFiles,
    checkboxInputChangeHandler,
    enforceRuntimeChangeHandler,
    otherInputsChangeHandler,
  ) => (
    <Content
      currentFileIndex={currentFileIndex}
      activeOs={activeOs}
      selectedFiles={selectedFiles}
      onCheckboxInputChange={checkboxInputChangeHandler}
      onEnforceRuntimeChange={enforceRuntimeChangeHandler}
      onOtherInputsChange={otherInputsChangeHandler}
    />
  );

  if (loading) return;

  if (error)
    return (
      <div className="dropped-files__content">
        <div className="dropped-files__content__error">Could not fetch data!</div>
      </div>
    );

  if (selectedFile === null)
    return (
      <div className="dropped-files__content">
        <div className="dropped-files__content__error">No data</div>
      </div>
    );

  return (
    <div className="dropped-files__content">
      <div className="dropped-files__content__header">
        <div className="dropped-files__content__file-name">
          <div className="dropped-files__content__icon-container">
            <FileIcon color="white" width={18} height={18} />
          </div>
          <p>{selectedFile?.value}</p>
          {checkShowSubmitButton(selectedFile.type) ? (
            <ActionButton
              icon={<SandboxIcon width={14} height={14} color="currentColor" />}
              // Still clickable without a licence, so the user is told why it
              // cannot be submitted; the cursor hints at it beforehand.
              style={!hasLicence ? { cursor: "not-allowed" } : undefined}
              onClick={submitToSandboxHandler}>
              Submit to Sandbox
            </ActionButton>
          ) : null}
        </div>
        <div className="dropped-files__content__clamcloudml">
          <ClamCloudInfo
            clamav={
              selectedFile?.clamav?.length > 0
                ? "Detected"
                : selectedFile?.clamav?.length === 0
                  ? "Clean"
                  : null
            }
            cdecision={
              typeof selectedFile?.cdecision === "undefined"
                ? "Unknown"
                : selectedFile?.cdecision.malicious
                  ? "Detected"
                  : "Clean"
            }
            mlcheck={
              selectedFile?.mlcheck?.malicious === 0
                ? "Clean"
                : selectedFile?.mlcheck?.malicious > 0
                  ? "Detected"
                  : "Unknown"
            }
            yara={
              selectedFile?.yara?.length
                ? { status: "Detected", value: selectedFile?.yara.length }
                : "Clean"
            }
          />
        </div>
      </div>

      {selectedFile?.details || selectedFile?.sha256 ? (
        <div className="dropped-files__content__mid">
          <InfoTable>
            {selectedFile?.details ? (
              <InfoRow label="Details Preview" value={selectedFile.details} />
            ) : null}
            {selectedFile?.sha256 ? (
              <InfoRow label="SHA256" value={selectedFile.sha256} mono={true} />
            ) : null}
          </InfoTable>
        </div>
      ) : null}

      <div className="dropped-files__content__bottom">
        <div className="dropped-files__content__table">
          <TabHeader active={activeTab} setActive={setActiveTab} />
          {activeTab && selectedFile ? <Details currentFile={selectedFile} /> : <RelatedVtis />}
        </div>
        {staticInfoDataList.length ? (
          <div className="dropped-files__content__static-results">
            {staticInfoDataList.map((data, i) => (
              <StaticInfoCardItem key={i} staticInfoCardData={data} onCollapse={collapseHandler} />
            ))}
          </div>
        ) : null}
      </div>
      {submitPopupActive ? (
        <Modal active={submitPopupActive} setActive={setSubmitPopupActive} type="submit-popup">
          <SubmitPopup
            selectedFiles={submitFile}
            setSelectedFiles={setSubmitFile}
            vmOptions={availableVmOptions}
            onClose={closePopupHandler}
            onSubmit={submitHandler}
            renderContent={renderContent}
            showApplyAll={false}
            showFileList={false}
          />
        </Modal>
      ) : null}
      <Backdrop show={artifactSubmissionLoading} message="Submitting artifact file..." />
    </div>
  );
};

export default DroppedFileListContent;
